import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const point = (event) => ({ x: event.clientX, y: event.clientY });
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #controller;
  #resizeObserver;
  #track;
  #index = 0;
  #offset = 0;
  #width = 0;
  #scale = 1;
  #x = 0;
  #y = 0;
  #pointers = new Map();
  #gesture;
  #pinch;
  #tap;
  #frame = 0;
  #wheelTimer;
  #previous;
  #next;
  #highRes = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog, media } = this.refs;
    this.#track = dialog.querySelector('.dialog-zoomed-gallery');
    dialog.dataset.ifViewer = '3';
    dialog.removeAttribute('on:keydown');
    media.forEach((item) => item.removeAttribute('on:click'));
    dialog.querySelectorAll('.inkforge-zoom-nav').forEach((button) => button.remove());
    this.#previous = this.#makeArrow(-1, 'Previous image', '‹');
    this.#next = this.#makeArrow(1, 'Next image', '›');
    dialog.addEventListener('keydown', (event) => this.handleKeyDown(event), { capture: true, signal });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
    dialog.addEventListener('close', () => this.#reset(), { signal });
    const options = { capture: true, passive: false, signal };
    this.#track.addEventListener('pointerdown', (event) => this.#pointerDown(event), options);
    this.#track.addEventListener('pointermove', (event) => this.#pointerMove(event), options);
    this.#track.addEventListener('pointerup', (event) => this.#pointerEnd(event), options);
    this.#track.addEventListener('pointercancel', (event) => this.#pointerEnd(event, true), options);
    this.#track.addEventListener('lostpointercapture', (event) => this.#pointerEnd(event, true), options);
    this.#track.addEventListener('wheel', (event) => this.#wheel(event), options);
    for (const type of ['touchstart', 'touchmove', 'touchend', 'touchcancel', 'click', 'dblclick', 'dragstart']) {
      this.#track.addEventListener(type, (event) => {
        if (!event.target.closest('drag-zoom-wrapper')) return;
        if (event.cancelable) event.preventDefault();
        event.stopImmediatePropagation();
      }, options);
    }
    this.#track.addEventListener('load', () => {
      this.#layoutImages();
      this.#renderImage();
    }, { capture: true, signal });
    this.#resizeObserver = new ResizeObserver(() => {
      if (!dialog.open) return;
      this.#cancelAnimation();
      this.#width = dialog.clientWidth;
      this.#setOffset(this.#index * this.#width);
      this.#layoutImages();
      this.#renderImage();
    });
    this.#resizeObserver.observe(dialog);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#cancelAnimation();
    clearTimeout(this.#wheelTimer);
    super.disconnectedCallback();
  }

  #makeArrow(direction, label, text) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `inkforge-zoom-nav inkforge-zoom-nav--${direction < 0 ? 'prev' : 'next'}`;
    button.setAttribute('aria-label', label);
    button.textContent = text;
    button.hidden = this.refs.media.length < 2;
    button.addEventListener('click', () => this.selectThumbnail(this.#index + direction), {
      signal: this.#controller.signal,
    });
    this.refs.dialog.append(button);
    return button;
  }

  open(index, event) {
    event?.preventDefault();
    this.#reset();
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.#width = this.refs.dialog.clientWidth;
    this.#layoutImages();
    this.refs.thumbnails.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  close() { this.closeDialog(); }

  closeDialog() {
    this.#reset();
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  #reset() {
    this.#cancelAnimation();
    clearTimeout(this.#wheelTimer);
    this.#pointers.clear();
    this.#gesture = this.#pinch = this.#tap = null;
    this.#resetImage();
  }

  #resetImage() {
    this.#scale = 1;
    this.#x = this.#y = 0;
    this.refs.dialog.classList.remove('if-viewer-zoomed', 'if-viewer-dragging');
    this.refs.media.forEach((item) => item.style.removeProperty('--if-viewer-transform'));
  }

  loadHighResolutionImage(container) {
    const image = container?.querySelector('img.product-media__image');
    const source = image?.getAttribute('data_max_resolution');
    if (!source || this.#highRes.has(source)) return;
    this.#highRes.add(source);
    const preload = new Image();
    preload.onload = () => {
      image.src = source;
      image.removeAttribute('srcset');
      this.#renderImage();
    };
    preload.onerror = () => this.#highRes.delete(source);
    preload.src = source;
  }

  handleThumbnailPointerEnter(index) { this.loadHighResolutionImage(this.refs.media[index]); }
  handleThumbnailClick(index) { this.selectThumbnail(index); }

  selectThumbnail(index, options = {}) {
    this.#cancelAnimation();
    clearTimeout(this.#wheelTimer);
    this.#resetImage();
    this.#index = clamp(Number(index) || 0, 0, this.refs.media.length - 1);
    const { media, thumbnails } = this.refs;
    media.forEach((item, i) => {
      item.classList.toggle('if-zoom-active', i === this.#index);
      item.setAttribute('aria-hidden', String(i !== this.#index));
      item.inert = i !== this.#index;
    });
    Array.from(thumbnails.children).forEach((button, i) => {
      button.setAttribute('aria-selected', String(i === this.#index));
      button.setAttribute('aria-current', String(i === this.#index));
    });
    const thumbnail = thumbnails.children[this.#index];
    if (thumbnail) thumbnails.scrollTo({
      left: thumbnail.offsetLeft - (thumbnails.clientWidth - thumbnail.clientWidth) / 2,
      behavior: options.behavior === 'instant' ? 'instant' : 'smooth',
    });
    this.#previous.disabled = this.#index === 0;
    this.#next.disabled = this.#index === media.length - 1;
    for (let i = Math.max(0, this.#index - 1); i <= Math.min(media.length - 1, this.#index + 1); i++) {
      const image = media[i].querySelector('drag-zoom-wrapper img');
      if (image) image.loading = 'eager';
      this.loadHighResolutionImage(media[i]);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#index));
    this.#animateTo(this.#index * this.#width, options.behavior === 'instant');
  }

  #setOffset(value) {
    this.#offset = value;
    this.#track.style.transform = `translate3d(${-value}px, 0, 0)`;
  }

  #cancelAnimation() {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #animateTo(target, instant = false) {
    this.#cancelAnimation();
    if (instant || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#setOffset(target);
      return;
    }
    const start = this.#offset;
    const startTime = performance.now();
    const duration = clamp(Math.abs(target - start) * 0.4, 180, 320);
    const tick = (now) => {
      const progress = clamp((now - startTime) / duration, 0, 1);
      this.#setOffset(start + (target - start) * (1 - Math.pow(1 - progress, 3)));
      this.#frame = progress < 1 ? requestAnimationFrame(tick) : 0;
    };
    this.#frame = requestAnimationFrame(tick);
  }

  #renderImage() {
    if (!this.refs.dialog.open) return;
    const item = this.refs.media[this.#index];
    const image = item?.querySelector('drag-zoom-wrapper img');
    if (!image) return;
    const maxX = Math.max(0, (image.offsetWidth * this.#scale - this.refs.dialog.clientWidth) / 2);
    const maxY = Math.max(0, (image.offsetHeight * this.#scale - this.refs.dialog.clientHeight) / 2);
    this.#x = this.#scale === 1 ? 0 : clamp(this.#x, -maxX, maxX);
    this.#y = this.#scale === 1 ? 0 : clamp(this.#y, -maxY, maxY);
    item.style.setProperty('--if-viewer-transform', `translate3d(${this.#x}px, ${this.#y}px, 0) scale(${this.#scale})`);
    image.draggable = false;
    this.refs.dialog.classList.toggle('if-viewer-zoomed', this.#scale > 1.01);
  }

  #layoutImages() {
    if (!this.refs.dialog.open) return;
    const width = this.refs.dialog.clientWidth;
    const height = Math.max(1, this.refs.dialog.clientHeight - 176);
    this.refs.media.forEach((item) => {
      const image = item.querySelector('drag-zoom-wrapper img');
      if (!image) return;
      const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || image.offsetWidth;
      const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || image.offsetHeight;
      if (!naturalWidth || !naturalHeight) return;
      const fit = Math.min(width / naturalWidth, height / naturalHeight);
      image.style.setProperty('width', `${naturalWidth * fit}px`, 'important');
      image.style.setProperty('height', `${naturalHeight * fit}px`, 'important');
    });
  }

  #zoom(nextScale, focus, previousFocus = focus) {
    const rect = this.refs.dialog.getBoundingClientRect();
    const next = clamp(nextScale, 1, 5);
    const ratio = next / this.#scale;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    this.#x = focus.x - cx - (previousFocus.x - cx - this.#x) * ratio;
    this.#y = focus.y - cy - (previousFocus.y - cy - this.#y) * ratio;
    this.#scale = next < 1.01 ? 1 : next;
    this.#renderImage();
  }

  #pointerDown(event) {
    if (event.button !== 0 || !event.target.closest('drag-zoom-wrapper')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    clearTimeout(this.#wheelTimer);
    this.#cancelAnimation();
    this.#pointers.set(event.pointerId, point(event));
    this.#track.setPointerCapture(event.pointerId);
    this.refs.dialog.classList.add('if-viewer-dragging');
    if (this.#pointers.size === 1) {
      this.#gesture = {
        start: point(event), previous: point(event), offset: this.#offset,
        index: this.#index, mode: this.#scale > 1 ? 'pan' : 'swipe',
        axis: null, moved: false, pinched: false, velocity: 0, time: event.timeStamp,
      };
    } else if (this.#pointers.size === 2) {
      this.#setOffset(this.#index * this.#width);
      const [a, b] = this.#pointers.values();
      this.#pinch = { distance: distance(a, b), midpoint: midpoint(a, b) };
      this.#gesture.pinched = true;
      this.#tap = null;
    }
  }

  #pointerMove(event) {
    if (!this.#pointers.has(event.pointerId)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#pointers.set(event.pointerId, point(event));
    if (this.#pointers.size >= 2) {
      const [a, b] = this.#pointers.values();
      const next = { distance: distance(a, b), midpoint: midpoint(a, b) };
      if (this.#pinch?.distance > 0) this.#zoom(
        this.#scale * next.distance / this.#pinch.distance, next.midpoint, this.#pinch.midpoint,
      );
      this.#pinch = next;
      return;
    }
    const gesture = this.#gesture;
    if (!gesture) return;
    const current = point(event);
    const dx = current.x - gesture.start.x;
    const dy = current.y - gesture.start.y;
    if (Math.hypot(dx, dy) > 6) gesture.moved = true;
    if (gesture.mode === 'pan') {
      this.#x += current.x - gesture.previous.x;
      this.#y += current.y - gesture.previous.y;
      this.#renderImage();
    } else if (gesture.moved) {
      if (!gesture.axis) gesture.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      if (gesture.axis === 'x') {
        const raw = gesture.offset - dx;
        const edge = clamp(raw, 0, (this.refs.media.length - 1) * this.#width);
        this.#setOffset(edge + (raw - edge) * 0.2);
        gesture.velocity = (current.x - gesture.previous.x) / Math.max(1, event.timeStamp - gesture.time);
      }
    }
    gesture.previous = current;
    gesture.time = event.timeStamp;
  }

  #pointerEnd(event, cancelled = false) {
    if (!this.#pointers.has(event.pointerId)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#pointers.delete(event.pointerId);
    if (this.#track.hasPointerCapture(event.pointerId)) this.#track.releasePointerCapture(event.pointerId);
    if (this.#pointers.size) {
      const [remaining] = this.#pointers.values();
      this.#gesture = {
        ...this.#gesture, start: remaining, previous: remaining, mode: 'pan',
        moved: true, pinched: true, time: event.timeStamp,
      };
      this.#pinch = null;
      return;
    }
    this.refs.dialog.classList.remove('if-viewer-dragging');
    const gesture = this.#gesture;
    this.#gesture = this.#pinch = null;
    if (!gesture) return;
    if (gesture.mode === 'swipe' && !gesture.pinched) {
      let target = Math.round(this.#offset / this.#width);
      const dx = event.clientX - gesture.start.x;
      const flick = event.timeStamp - gesture.time < 100 && Math.abs(gesture.velocity) > 0.45;
      if (!cancelled && gesture.axis === 'x' && target === gesture.index &&
          (Math.abs(dx) > Math.min(120, this.#width * 0.18) || flick)) {
        target = gesture.index + (dx < 0 ? 1 : -1);
      }
      this.selectThumbnail(cancelled ? gesture.index : target);
    }
    if (cancelled || gesture.moved || gesture.pinched) { this.#tap = null; return; }
    const current = point(event);
    if (event.pointerType === 'mouse') {
      this.#zoom(this.#scale > 1 ? 1 : 2.5, current);
    } else if (this.#tap && event.timeStamp - this.#tap.time < 300 && distance(this.#tap, current) < 35) {
      this.#zoom(this.#scale > 1 ? 1 : 2.5, current);
      this.#tap = null;
    } else {
      this.#tap = { ...current, time: event.timeStamp };
    }
  }

  #wheel(event) {
    if (!event.target.closest('drag-zoom-wrapper')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!event.ctrlKey && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      if (this.#scale > 1) {
        this.#x -= event.deltaX;
        this.#renderImage();
      } else {
        this.#cancelAnimation();
        this.#setOffset(clamp(this.#offset + event.deltaX, 0, (this.refs.media.length - 1) * this.#width));
        clearTimeout(this.#wheelTimer);
        this.#wheelTimer = setTimeout(() => this.selectThumbnail(Math.round(this.#offset / this.#width)), 120);
      }
    } else {
      this.#cancelAnimation();
      this.#setOffset(this.#index * this.#width);
      this.#zoom(this.#scale * Math.exp(-event.deltaY * 0.002), point(event));
    }
  }

  handleKeyDown(event) {
    const rect = this.refs.dialog.getBoundingClientRect();
    const center = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    if (event.key === 'Escape') this.close();
    else if (['+', '=', '-', '0'].includes(event.key)) {
      this.#zoom(event.key === '0' ? 1 : this.#scale * (event.key === '-' ? 0.8 : 1.25), center);
    } else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      if (this.#scale > 1) {
        this.#x += event.key === 'ArrowLeft' ? 80 : event.key === 'ArrowRight' ? -80 : 0;
        this.#y += event.key === 'ArrowUp' ? 80 : event.key === 'ArrowDown' ? -80 : 0;
        this.#renderImage();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        this.selectThumbnail(this.#index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    } else if (event.key === 'Home' || event.key === 'End') {
      this.selectThumbnail(event.key === 'Home' ? 0 : this.refs.media.length - 1);
    } else return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
